import React, { useMemo } from 'react';

import { SECTIONS, COMPONENTS } from 'components/Globals/Analytics/constants';
// NOTE: we need to handle below es lint dependency cycle, currently suppressing it for git commit
// eslint-disable-next-line import/no-cycle
import EntityContactBlock from 'components/Globals/EntityContactBlock';
import Content from 'components/Globals/Layout/StubPage/Content';
import VideoListing, { VIDEO_SECTION_TYPES } from 'components/Media/Display/VideoListing';
import EntityRentalsList from 'components/Productions/Display/EntityRentalsList';
import EntityUpcomingPerformances from 'components/Productions/Display/EntityUpcomingPerformances';
import FeaturedProductions from 'components/Productions/Display/FeaturedProductions';
import CreatorWorkList from 'components/Work/Display/CreatorWorkList';

import { TP, ORGANIZATION_TYPE_IDS, SORT_OPTION_TYPES } from 'constants/index';

import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import { useInfiniteQuery } from 'utils/react-query';

import mediaQueries from 'containers/Media/queries';

import { useTranslation } from 'src/i18n';

import AboutOrg from '../AboutOrg';
import AssociatedOrgs from '../AssociatedOrgs';
import AgencyRosterView from '../AgencyRosterView';

const Home = ({ entityType, entity }) => {
  const { t } = useTranslation('NS_ENTITY_STUB_PAGE');
  const { obRouteContext } = useAppContext();
  const { isDesktop } = useDeviceTypeLayouts();
  const { filterParams } = obRouteContext || {};

  // NOTE: reason of using useInfiniteQuery instead of useQuery is to get the data in same order as per ProductionListing.js and ssr level data
  const { data: hasDigitalVideos } = useInfiniteQuery(
    mediaQueries.getDigitalVideos({
      entityId: entity?.id,
      entityType,
      limit: 4,
      filters: {
        ...filterParams,
        sort: SORT_OPTION_TYPES.YEAR_DESC.value,
      },
      queryConfig: {
        select: response => {
          const videosCount = response?.pages?.[0]?.data?.length;

          if (isDesktop) {
            return videosCount >= 4;
          }

          return videosCount > 0;
        },
        enabled: entity?.stats?.media?.videos?.digital?.exists,
      },
    }),
  );
  const sections = useMemo(() => {
    const stats = entity?.stats || {};

    const components = [
      {
        component: CreatorWorkList,
        props: {
          enableInfiniteScrolling: false,
          limit: 5,
        },
        placement: {
          desktop: {
            column: 1,
            order: 1,
          },
          tablet: {
            column: 1,
            order: 1,
          },
          mobile: {
            column: 1,
            order: 1,
          },
        },
        showSection: stats?.creations?.composer?.exists,
      },
      {
        component: EntityUpcomingPerformances,
        props: {
          showFilters: stats?.isPro && !isDesktop,
        },
        placement: {
          desktop: {
            column: 1,
            order: 2,
          },
          tablet: {
            column: 1,
            order: 2,
          },
          mobile: {
            column: 1,
            order: 2,
          },
        },
        showSection: stats?.productions?.future?.exists,
      },
      {
        component: FeaturedProductions,
        props: {
          limit: 4,
          trackingData: {
            section: SECTIONS.FEATURED_PHOTOS,
            component: COMPONENTS.MEDIA_CARD,
          },
        },
        placement: {
          desktop: {
            column: 1,
            order: 3,
          },
          tablet: {
            column: 1,
            order: 3,
          },
          mobile: {
            column: 1,
            order: 3,
          },
        },
        sectionProps: {
          title: t(`${TP}.FN_FEATURED_PAST_PERFORMANCES`),
          enableSeeAll: false,
        },
        showSection: stats?.productions?.past?.exists,
      },
      {
        component: VideoListing,
        props: {
          limit: 4,
          type: VIDEO_SECTION_TYPES.DIGITAL,
          trackingData: {
            section: SECTIONS.DIGITAL_VIDEOS,
            component: COMPONENTS.MEDIA_CARD,
          },
        },
        placement: {
          desktop: {
            column: 1,
            order: 4,
          },
          tablet: {
            column: 1,
            order: 4,
          },
          mobile: {
            column: 1,
            order: 4,
          },
        },
        sectionProps: {
          enableSeeAll: false,
        },
        showSection: entity?.stats?.media?.videos?.digital?.exists && hasDigitalVideos
      },
      {
        component: VideoListing,
        props: {
          limit: 3,
          isOverview: true,
          title: t(`${TP}.FN_FEATURED_PERFORMANCE_VIDEOS`),
          trackingData: {
            section: SECTIONS.FEATURED_VIDEOS,
            component: COMPONENTS.MEDIA_CARD,
          },
        },
        placement: {
          desktop: {
            column: 1,
            order: 5,
          },
          tablet: {
            column: 1,
            order: 5,
          },
          mobile: {
            column: 1,
            order: 5,
          },
        },
        showSection: stats?.media?.videos?.production?.exists,
      },
      {
        component: EntityRentalsList,
        props: {
          limit: 3,
          enableSeeAll: true,
          disabled: true,
          isOverview: true,
          title: t('PRODUCTION_RENTALS'),
        },
        placement: {
          desktop: {
            column: 1,
            order: 6,
          },
          tablet: {
            column: 1,
            order: 6,
          },
          mobile: {
            column: 1,
            order: 6,
          },
        },
        showSection: stats?.rentals?.exists,
      },
      {
        component: AboutOrg,
        props: {
          showMinDescription: true,
        },
        sectionProps: {
          enableSeeAll: false,
        },
        placement: {
          desktop: {
            column: 1,
            order: 7,
          },
          tablet: {
            column: 1,
            order: 7,
          },
          mobile: {
            column: 1,
            order: 7,
          },
        },
        showSection: stats?.about?.exists,
      },
      {
        component: AssociatedOrgs,
        props: {
          enableSeeAll: false,
          isOverview: true,
          excludeVenues: true,
          title: t(`${TP}.FN_ASSOCIATED_ORGS`),
        },
        placement: {
          desktop: {
            column: 1,
            order: 8,
          },
          tablet: {
            column: 1,
            order: 8,
          },
          mobile: {
            column: 1,
            order: 8,
          },
        },
        showSection: stats?.associatedOrgs?.exists,
      },
      {
        component: AssociatedOrgs,
        props: {
          enableSeeAll: false,
          isOverview: true,
          onlyVenues: true,
          title: t(`${TP}.m_VENUES`),
        },
        placement: {
          desktop: {
            column: 1,
            order: 9,
          },
          tablet: {
            column: 1,
            order: 9,
          },
          mobile: {
            column: 1,
            order: 9,
          },
        },
        showSection: entity?.organizationType?.id !== ORGANIZATION_TYPE_IDS.VENUE && stats?.venues?.exists,
      },
      {
        component: AgencyRosterView,
        props: {},
        placement: {
          desktop: {
            column: 1,
            order: 2,
          },
          tablet: {
            column: 1,
            order: 2,
          },
          mobile: {
            column: 1,
            order: 2,
          },
        },
        showSection: entity?.organizationType?.id === ORGANIZATION_TYPE_IDS.AGENCY,
      },
      {
        component: EntityContactBlock,
        props: {
          enableSeeAll: false,
          isOverview: true,
        },
        placement: {
          desktop: {
            column: 1,
            order: 10,
          },
          tablet: {
            column: 1,
            order: 10,
          },
          mobile: {
            column: 1,
            order: 10,
          },
        },
        showSection: stats?.contacts?.exists || stats?.agencies?.exists,
      },
    ];

    return components;
  }, [entity?.organizationType?.id, entity?.stats, hasDigitalVideos, t]);

  return <Content entity={entity} entityType={entityType} sections={sections} />;
};

export default Home;
